// Play table (P2 §10) — the SINGLE play surface: you (seat 0) vs 1..5 mechanical CPUs (= 1〜6-player
// mahjong), driven by the authoritative engine reducer via TableGame. CPUs are NOT a playing AI —
// tsumogiri or the owner-sanctioned yakuman collector (spec §0). This panel owns the play-screen IA:
//   HUD (☰ / 場況圧縮 / 人数 / 新規 / ⚙) → 卓 (SeatBoard + center-plate, D16 の唯一の正出所)
//   → 手牌トレイ (2 段折返し・2 タップ打牌 D18・アクションバー) の縦 3 段 (§10.2)。
// 旧コントロールバーの 16 要素は削除ゼロで移設 (§10.1): QoL/プリセット/回転卓 → 対局設定ドロワー、
// seed/無限牌/CPU方針/ドラ指定/初期自摸/警告 → ドロワー最下部の検証ツール、msg → トースト。
// 演出は useTableFx(journal 駆動) + FxOverlay、結果は段階開示モーダル (fx.resultStage 制御)。
import {
  useCallback, useEffect, useRef, useState, useSyncExternalStore,
  type CSSProperties, type PointerEvent as ReactPointerEvent, type ReactNode,
} from 'react';
import type { Intent, Meld, Seat, TileId, PlayerCount, Stamp } from '@mj/protocol';
import { compareTiles, baseOfAka } from '@mj/protocol';
import { effectiveLimitMode, shanten, reachableYaku, progressToFace, type DailyProgress } from '@mj/engine';
import { recordMatchPlayed } from '../account/dailyStore';
import { makeResultName } from '../yakuName';
import { limitLabelFor } from '../solo/soloSession';
import { useStudioStore, useStudioApi } from '../useStore';
import { TileView } from '../render/TileView';
import { MeldView } from '../render/MeldView';
import { TileFan } from '../render/TileFan';
import { SeatBoard } from '../render/SeatBoard';
import { fitRow, useElementSize } from '../ui/fit';
import { TableGame } from '../play/tableGame';
import { LocalTable } from '../play/localTable';
import type { PlayTable } from '../play/playTable';
import { RemoteTable } from '../play/remoteTable';
import { WaitingRoom } from '../ui/WaitingRoom';
import { errJa } from './errJa';
import { useOnlineSession, getOnlineTable, exitOnline, type ExitTo } from '../net/onlineSession';
import { seatViewFromView } from '../render/seatViewFromView';
import { consumePendingDemo } from '../play/pendingStart';
import { doraSummary } from '../play/doraSummary';
import { riichiAvailable } from '../play/riichiable';
import { RULESET_PRESETS, buildPreset } from '../presets';
import { listYakumanTargets } from '../play/collector';
import { useTableFx } from '../ui/fx';
import { FxOverlay } from '../ui/CutIn';
import { sfx } from '../ui/sound';
import { getSettings, patchSettings, useSettings, useEffectiveReduced } from '../ui/settings';
import { nextReveal } from '../onboarding/triggers';
import { useSeen, markSeen } from '../onboarding/seen';

const sorted = (t: TileId[]) => [...t].sort(compareTiles);
const randomSeed = () => crypto.getRandomValues(new Uint32Array(1))[0]!;
const WIND: Record<string, string> = { z1: '東', z2: '南', z3: '西', z4: '北' };

// HOLE #12: preset-stamp labels for the picker/bubbles. Wire is a bare enum (no game info); the emoji
// is purely cosmetic and lives client-side only.
const STAMP_LABEL: Record<Stamp, string> = {
  hello: '👋', thanks: '🙏', sorry: '🙇', nice: '👍', gg: '🎉',
  oops: '😅', think: '🤔', wait: '✋', wow: '😲', lol: '😂',
};
const STAMP_ORDER: Stamp[] = ['hello', 'thanks', 'sorry', 'nice', 'gg', 'oops', 'think', 'wait', 'wow', 'lol'];

// 牌の和名 (選択中表示・槓ボタン用)。色覚に依らない読み上げにも使う。
const NUM_JA = ['一', '二', '三', '四', '五', '六', '七', '八', '九'] as const;
const SUIT_JA: Record<string, string> = { m: '萬', p: '筒', s: '索' };
const HONOR_JA: Record<string, string> = { z1: '東', z2: '南', z3: '西', z4: '北', z5: '白', z6: '發', z7: '中' };
const FLOWER_JA: Record<string, string> = { f1: '春', f2: '夏', f3: '秋', f4: '冬', f5: '梅', f6: '蘭', f7: '菊', f8: '竹' };
function tileName(t: TileId): string {
  const b = baseOfAka(t);
  if (b.startsWith('z')) return HONOR_JA[b] ?? b;
  if (b.startsWith('f')) return FLOWER_JA[b] ?? b;
  const rank = Number(b[1]);
  return `${t.endsWith('r') ? '赤' : ''}${NUM_JA[rank - 1] ?? b[1]!}${SUIT_JA[b[0]!] ?? ''}`;
}

// D2 z-index トークン (CSSProperties.zIndex は number 型なので cast して var() を通す)
const Z_HUD = 'var(--z-hud)' as CSSProperties['zIndex'];
const Z_BAND = 'var(--z-band)' as CSSProperties['zIndex'];
const Z_MODAL = 'var(--z-modal)' as CSSProperties['zIndex'];

// 暗色フィールド (ドロワー/HUD の input・select 共通)。theme.css に専用クラスが無いので
// 既存流儀どおり inline + var(--トークン) で書く。
const FIELD: CSSProperties = {
  background: 'var(--surface-well)', color: 'var(--text-hi)',
  border: '1px solid var(--border-panel)', borderRadius: 6,
  padding: '4px 8px', fontSize: 13, fontFamily: 'inherit', minHeight: 30, boxSizing: 'border-box',
};

/** matchMedia 購読フック (App.tsx と同形。jsdom/SSR では false に倒れる)。 */
function useMediaQuery(query: string): boolean {
  const subscribe = useCallback((cb: () => void) => {
    try {
      const m = window.matchMedia(query);
      m.addEventListener('change', cb);
      return () => m.removeEventListener('change', cb);
    } catch { return () => {}; }
  }, [query]);
  const getSnapshot = useCallback(() => {
    try { return window.matchMedia(query).matches; } catch { return false; }
  }, [query]);
  return useSyncExternalStore(subscribe, getSnapshot);
}

export function SoloMahjong() {
  const api = useStudioApi();
  const revision = useStudioStore((s) => s.revision);
  const catalog = useStudioStore((s) => s.catalog);
  const limitMode = useStudioStore((s) => effectiveLimitMode(s.ruleSet));
  const infinite = useStudioStore((s) => s.ruleSet.tiles.infinite === true);
  const settings = useSettings();
  const reduced = useEffectiveReduced();
  const resultName = makeResultName(catalog);
  // online 対局: lobby が enterOnline() で登録した RemoteTable。非 null の間は同じ盤面を online 駆動する
  // (ローカル自動配牌は抑止・ローカル専用 UI は隠す)。[[unified-play-surface]] 一画面・両モード。
  const onlineTable = useOnlineSession();
  const online = onlineTable !== null;
  const [playerCount, setPlayerCount] = useState<PlayerCount>(4);
  const [seedInput, setSeedInput] = useState(() => String(randomSeed()));
  // riichi declaration MODE (pick one, then discard): '' | 立直 | オープン立直 | 無限立直 | オープン無限立直
  const [riichiMode, setRiichiMode] = useState<'' | 'riichi' | 'open' | 'infinite' | 'openInfinite'>('');
  const [cpuPolicy, setCpuPolicy] = useState('tsumogiri'); // 'tsumogiri' | 'random' | <yakuman id>
  // ドラ指定: comma-separated tile ids that FORCE the revealed dora indicators (testing). Empty = random.
  const [doraInput, setDoraInput] = useState(() => (api.getState().ruleSet.tiles.fixedDoraIndicators ?? []).join(','));
  // 初期自摸: comma-separated tile ids that FORCE seat 0's draw sequence (testing). Empty = normal.
  const [drawsInput, setDrawsInput] = useState(() => (api.getState().ruleSet.tiles.fixedDraws ?? []).join(','));
  const yakumanTargets = listYakumanTargets(catalog);
  const [msg, setMsg] = useState(''); // §10.1-16: トースト + aria-live で表示
  // HOLE #19: 段階開示 — 席役牌ヒントチップ (N>4 初回のみ, liveToday=true の唯一の概念)
  const onboardingSeen = useSeen();
  const [seatYakuHint, setSeatYakuHint] = useState<string | null>(null);
  // Daily reward toast: shown inline on match end. Cleared on next game start.
  const [dailyReward, setDailyReward] = useState<{ en: number; progressPt: number; progress: DailyProgress } | null>(null);
  const dailyShownForRef = useRef<string | null>(null); // "gameId:kyoku" dedupe key
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [stampPickerOpen, setStampPickerOpen] = useState(false);
  // 2 タップ打牌 (D18 'double'): 1 タップ目で選択した牌 (key は描画キー — 同一牌IDの重複対策)
  const [armed, setArmed] = useState<{ key: string; tile: TileId } | null>(null);
  // 「新規」の二度押し確認 (タッチのみ, §10.2)
  const [newArmed, setNewArmed] = useState(false);
  const newTimerRef = useRef<number | null>(null);
  const gameRef = useRef<PlayTable | null>(null);
  const gameIdRef = useRef(0); // 配牌カスケードの再 mount キー用 (kyoku は新規対局で 0 に戻るため)
  // 手牌バンドの 2 パス fit (§3): 右トレイ(副露+花扇+北扇)幅を実測 → 残り幅で concealed 手牌を
  // fitRow(shrunk) し、全牌を改行なし 1 行・クリック可で収める。box は useElementSize で計測。
  const handBandRef = useRef<HTMLDivElement>(null);
  const trayRef = useRef<HTMLDivElement>(null);
  const band = useElementSize(handBandRef);
  const tray = useElementSize(trayRef);
  // 対局サーフェス全体の実寸 (回転後キャンバス) を計測。レイアウト判断はこれを基準にする (下記)。
  const rootRef = useRef<HTMLDivElement>(null);
  const surface = useElementSize(rootRef);
  const [, force] = useState(0);
  // stable so it can be registered as a PlayTable subscriber once per table (online pushes re-renders).
  const bump = useCallback(() => force((n) => n + 1), []);
  // 安定な onClose 参照 — ドロワーの focus-trap effect が onClose を依存に持つため、毎 render
  // (bump() ごと) 再生成すると effect が張り直されてフォーカスを奪い続ける。useCallback で固定する。
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);
  const closeMenu = useCallback(() => setMenuOpen(false), []);

  const isXSRaw = useMediaQuery('(max-width: 479px)');
  const shortLandRaw = useMediaQuery('(max-height: 500px)'); // 横持ち圧縮 (§10.2)
  const coarse = useMediaQuery('(pointer: coarse)');
  // 強制横画面(CSS 90°回転)対応: レイアウト判断は実 viewport ではなく**計測した実キャンバス寸法**で行う。
  // 回転時、useMediaQuery は実 viewport(縦) を見て shortLand を誤判定し、盤面 minHeight(300) が
  // 回転後キャンバス高(=実 viewport 幅)を溢れて「見切れ」が起きていた。useElementSize はレイアウト寸法
  // (=回転後キャンバス) を返すので回転を自動吸収する (初回計測前のみ media query にフォールバック)。
  const isXS = surface.w > 0 ? surface.w <= 479 : isXSRaw;
  const shortLand = surface.h > 0 ? surface.h <= 500 : shortLandRaw;

  // 演出パイプライン (§10.5): journal 駆動。reduced は D15 実効値、役名はカタログ写像。
  const { fx, skip, ariaLines } = useTableFx(gameRef.current, reduced, resultName);

  const start = (seed: number, players: PlayerCount) => {
    const st = api.getState();
    const tg = new TableGame(st.ruleSet, seed, players, st.catalog);
    const q = getSettings().qol; // QoL hydrate (§10.5): 永続値を新しい卓へ引き継ぐ
    tg.skipHumanCalls = q.skipCalls;
    tg.autoWin = q.autoWin;
    tg.autoNuki = q.autoNuki;
    tg.setCpuPolicy(cpuPolicy); // carry the CPU policy across re-deals
    gameRef.current?.dispose();
    // wrap in the LocalTable PlayTable adapter so the board reads g.view() like online play does.
    const lt = new LocalTable(tg);
    lt.subscribe(bump);
    gameRef.current = lt;
    gameIdRef.current += 1;
    setRiichiMode(''); setArmed(null); setMsg(''); setDailyReward(null); dailyShownForRef.current = null; bump();
  };
  // (re)deal a fresh random game on mount and whenever the seat count changes — LOCAL only.
  // online のときは lobby が用意した RemoteTable を使うので自動配牌しない (上書きしてしまう)。
  // デモ卓 (HOLE #18): App が play 遷移前に予約した人数を consume し、その人数で初回配牌する (race-free)。
  useEffect(() => {
    if (getOnlineTable()) return;
    const demoN = consumePendingDemo();
    // 予約人数が現状と違えば setPlayerCount → この effect が再走して下の配牌へ落ちる (二重配牌を避け return)。
    if (demoN != null && demoN !== playerCount) { setPlayerCount(demoN as PlayerCount); return; }
    const n = (demoN ?? playerCount) as PlayerCount;
    const seed = randomSeed();
    setSeedInput(String(seed));
    start(seed, n);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, playerCount]);
  // online セッションが入ったら盤面をその RemoteTable に切替える (既存ローカル卓は破棄)。online を抜けた
  // (onlineTable→null) ときは、盤面が抜け殻の RemoteTable を指したままにならないよう、ローカル卓へ復帰する
  // (退出ナビは exitOnline がロビー/タイトルへ飛ばす — ここはあくまで盤面の中身を有効に保つだけ)。
  useEffect(() => {
    if (!onlineTable) {
      if (gameRef.current instanceof RemoteTable) start(randomSeed(), playerCount);
      return;
    }
    if (gameRef.current && gameRef.current !== onlineTable) gameRef.current.dispose();
    gameRef.current = onlineTable;
    gameIdRef.current += 1;
    const unsub = onlineTable.subscribe(bump);
    setRiichiMode(''); setArmed(null); setMsg(''); bump();
    return () => { unsub(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onlineTable]);
  // save→reflect: rule edits apply from the NEXT hand. online はルールがサーバ権威なので無視。
  useEffect(() => {
    if (online) return;
    const g0 = gameRef.current;
    if (!g0) return;
    const st = api.getState();
    g0.applyRules?.(st.ruleSet, st.catalog);
    bump();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revision]);
  // 音量 hydrate (§10.5)。App もやるが、play 単体 mount でも成立するよう自衛で再適用。
  useEffect(() => {
    const s = getSettings();
    sfx.setVolume(s.audio.muted ? 0 : s.audio.masterVolume / 100);
  }, []);
  // shell seam: タイトル「人数・ルールを選んで開始」→ 対局設定ドロワー自動オープン (App.tsx 発火)
  useEffect(() => {
    const onOpen = () => setDrawerOpen(true);
    window.addEventListener('mj:open-game-settings', onOpen);
    return () => window.removeEventListener('mj:open-game-settings', onOpen);
  }, []);
  // (デモ卓の配牌は mount effect 側で consumePendingDemo() により race-free に処理する — HOLE #18 修正。
  //  旧 window イベント方式はリスナ登録前に発火する race があったため撤去した。)
  // msg はトースト: 3 秒で自動消滅 (§10.1-16)
  useEffect(() => {
    if (!msg) return;
    const t = window.setTimeout(() => setMsg(''), 3000);
    return () => window.clearTimeout(t);
  }, [msg]);
  // HOLE #19: 席役牌ヒントチップ — N>4 の卓が初めて描画された瞬間に一度だけ表示。
  // nextReveal が "1つだけ" を強制; N≤4 パスはこの effect が何もしないので byte-identical。
  useEffect(() => {
    if (playerCount <= 4) return;
    const seenSet = new Set(
      Object.entries(onboardingSeen.seen)
        .filter(([, v]) => v)
        .map(([k]) => k as import('../onboarding/triggers').ConceptId),
    );
    const trigger = nextReveal(seenSet, ['seatYaku']);
    if (trigger) setSeatYakuHint(trigger.copy);
  }, [playerCount, onboardingSeen]);
  // online: 非 fatal エラー (NOT_YOUR_TURN / ILLEGAL_MOVE / NOT_HOST 等) が無音にならないようトーストへ。
  // RemoteTable は subscribe で再描画を押すので、変化を ref で検知して 1 回だけ toast に出す。fatal は別 (終端モーダル)。
  const onlineErr = online ? onlineTable?.lastError ?? null : null;
  const lastErrRef = useRef<string | null>(null);
  useEffect(() => {
    if (onlineErr && onlineErr !== lastErrRef.current) setMsg(onlineErr);
    lastErrRef.current = onlineErr;
  }, [onlineErr]);
  // HOLE #13: host-handoff toast. RoomState.host already flips on the wire when the current host
  // leaves/drops (reassigned to the lowest still-occupied seat) and roomState→notify re-renders us.
  // Detect the change via a ref and surface ONE toast. Skip the first observed value (initial host is
  // not a "handoff") and skip null (pre-roomState). me === host → "あなた"; else seat label.
  const remoteForHost = onlineTable instanceof RemoteTable ? onlineTable : null;
  const hostSeat = remoteForHost?.host ?? null;
  const lastHostRef = useRef<number | null>(null);
  useEffect(() => {
    if (hostSeat === null) return;                       // no roomState yet — nothing to compare.
    const prev = lastHostRef.current;
    lastHostRef.current = hostSeat;
    if (prev === null || prev === hostSeat) return;      // first observation or no change → no toast.
    const myHumanSeat = remoteForHost?.humanSeat ?? -1;
    setMsg(hostSeat === myHumanSeat ? 'あなたがホストになりました' : `ホストが 席${hostSeat + 1} に交代しました`);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hostSeat]);
  useEffect(() => () => { if (newTimerRef.current !== null) window.clearTimeout(newTimerRef.current); }, []);
  // HOLE #20: daily reward — fire once per match end (handOver or gameOver).
  // Deduped by "gameId:kyoku" so a re-render from bump() does not double-fire.
  // Uses the current game snapshot at call time — g is read below the hook call
  // so we read it directly from gameRef.current here.
  useEffect(() => {
    const gNow = gameRef.current;
    if (!gNow) return;
    if (gNow.wait !== 'handOver' && gNow.wait !== 'gameOver') return;
    const dedupeKey = `${gameIdRef.current}:${gNow.kyoku}`;
    if (dailyShownForRef.current === dedupeKey) return;
    dailyShownForRef.current = dedupeKey;
    try {
      const { reward, progress } = recordMatchPlayed();
      setDailyReward({ en: reward.en, progressPt: reward.progressPt, progress });
    } catch {
      // Best-effort: never let a daily-store error break the match flow.
    }
  }); // No dep array — fires after every render; the dedupeKey ref prevents double-fire.
  // HOLE #12: stamp bubble auto-dismiss: re-render every second while there are active stamps.
  const remoteForStamps = onlineTable instanceof RemoteTable ? onlineTable : null;
  useEffect(() => {
    if (!remoteForStamps) return;
    const stamps = remoteForStamps.stamps;
    if (stamps.length === 0) return;
    const t = window.setInterval(() => bump(), 1000);
    return () => window.clearInterval(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remoteForStamps, remoteForStamps?.stamps.length]);

  const g = gameRef.current;
  if (!g) return <div style={{ padding: 24, color: 'var(--text-mid)' }}>準備中…</div>;
  // online 待機ロビー (S6): 部屋作成と対局開始を分離。最初の snapshot で 'playing' に遷移するまで
  // WaitingRoom を表示し、ホストの開始操作 (RemoteTable.start) を待つ。盤面は phase==='playing' で出す。
  // (g 本体の型は PlayTable のまま保つため、RemoteTable 判定はローカル変数で受ける。)
  const remote = g instanceof RemoteTable ? g : null;
  if (remote !== null && remote.phase === 'waiting') {
    // waiting/gameOver lobby: leave with NO confirmation (no live match), then route to the online lobby.
    // Do NOT spawn a ghost local solo table — exitOnline only tears down + navigates.
    const leaveWaiting = () => exitOnline('online');
    return <WaitingRoom table={remote} onLeave={leaveWaiting} />;
  }
  const view = g.view(); // redacted GameStateView — same shape local & online
  const me = g.humanSeat;
  const human = view.players[me];
  // online: RemoteTable は最初の snapshot 到着まで空 view を返す → 接続待ち表示 (hooks は全て上で実行済)。
  if (!human) return <div style={{ padding: 24, color: 'var(--text-mid)' }}>接続中…</div>;
  const myHand = human.hand ?? []; // own concealed hand (always present for the viewer)
  const drawn = g.humanDrawn();
  // The drawn tile is IN the hand only in the 'discard' phase (the 嶺上 tile after a kan). In the 'draw'
  // phase it's the wall-top PEEK (not yet drawn) — using hand.includes() there is WRONG: when the peek
  // matches a tile you already hold, it would drop a tile from effHand and break tenpai/riichi detection.
  const drawnInHand = drawn !== null && view.phase === 'discard';
  const moves = g.humanMoves();
  // kan candidates are computed from the 14-tile hand (incl. the freshly-drawn tile), since legalMoves
  // only sees the pre-draw 13 — otherwise a "3 in hand + draw the 4th" ankan/加槓 is never offered.
  const ankans = g.humanAnkanTiles();
  const shouminkans = g.humanShouminkanTiles();
  // 北抜き / 西抜き candidates (nukidora) + 花抜き (花牌任意化) — same 14-tile basis.
  const nukis = g.humanNukiTiles();
  const flowerPulls = g.humanFlowerTiles();
  const canKyuushuu = moves.some((m) => m.kind === 'kyuushuu');
  const menzen = !human.melds.some((m) => m.kind !== 'ankan' && m.kind !== 'kita');
  const locked = !!human.riichi?.declared && !human.riichi?.infinite; // 立直後ツモ切りのみ（無限立直は手出し可）

  // play-assist: on your turn, the 14-tile hand (hand + drawn); badge the discards that REACH tenpai.
  const onTurn = g.wait === 'turn';
  const effHand = onTurn && drawn && !drawnInHand ? sorted([...myHand, drawn]) : sorted(myHand);
  const handTiles = (() => {
    const h = sorted(myHand);
    if (drawnInHand && drawn) h.splice(h.indexOf(drawn), 1);
    return h;
  })();
  const tenpaiCuts = new Set<TileId>();
  let curShanten = 99;
  if (onTurn) {
    curShanten = shanten(effHand, human.melds);
    for (const t of new Set(effHand)) {
      const rest = effHand.slice();
      rest.splice(rest.indexOf(t), 1);
      if (shanten(rest, human.melds) === 0) tenpaiCuts.add(t);
    }
  }
  // near-yaku assist (推定): a few REACHABLE yaku ranked by VALUE — HEURISTIC (see reachableYaku docs).
  const nearYaku = onTurn
    ? reachableYaku(
        effHand,
        human.melds,
        { roundWind: view.roundWind, seatWind: human.seatWind, menzen },
        api.getState().ruleSet,
        catalog,
        { limit: 5 },
      )
    : [];
  const rs0 = api.getState().ruleSet;
  // 無限立直: honor 点棒なし立直 (riichiBelowStick) + 再立直 (reRiichi) — the engine accepts both.
  const riichiable = riichiAvailable({
    onTurn, menzen, declared: !!human.riichi?.declared, tenpaiReachable: tenpaiCuts.size > 0,
    score: human.score, riichiStick: rs0.points.riichiStick,
    riichiBelowStick: rs0.flags.riichiBelowStick === true, reRiichi: rs0.flags.reRiichi === true,
  });

  const tryAct = (intent: Intent) => {
    sfx.play('uiClick'); // §10.5: 操作音 (打牌の着地音/強打は fx パイプラインが journal から鳴らす)
    const r = g.act(intent);
    if (!r.ok) setMsg(r.error ?? 'illegal');
    else { setMsg(''); setRiichiMode(''); }
    setArmed(null);
    bump();
  };
  const discard = (tile: TileId) => {
    if (g.wait !== 'turn') return;
    const intent: Intent = { kind: 'discard', tile, tsumogiri: tile === drawn };
    if (riichiMode) { intent.riichi = true; if (riichiMode === 'open' || riichiMode === 'openInfinite') intent.openRiichi = true; if (riichiMode === 'infinite' || riichiMode === 'openInfinite') intent.infiniteRiichi = true; }
    tryAct(intent);
  };
  // 打牌方式 (D18): 'double' は 1 タップ目=選択 (data-selected)、2 タップ目=打牌。
  const confirmDouble = settings.input.discardConfirm === 'double';
  const tapTile = (key: string, tile: TileId) => {
    if (g.wait !== 'turn') return;
    if (confirmDouble && armed?.key !== key) {
      sfx.play('uiClick');
      setArmed({ key, tile });
      return;
    }
    discard(tile);
  };
  // ドラ指定/初期自摸を適用して即再配牌（無効な tile id は無視。空なら通常ランダムに戻す）。
  const applyDora = () => {
    const list = doraInput.split(',').map((x) => x.trim()).filter(Boolean)
      .filter((x) => /^([mps][1-9]|m5r|p5r|s5r|z[1-7]|f[1-8])$/.test(x)) as TileId[];
    api.getState().patchTiles({ fixedDoraIndicators: list.length ? list : undefined });
    const s = Number.parseInt(seedInput, 10) || randomSeed();
    start(s, playerCount);
  };
  const applyDraws = () => {
    const list = drawsInput.split(',').map((x) => x.trim()).filter(Boolean)
      .filter((x) => /^([mps][1-9]|m5r|p5r|s5r|z[1-7]|f[1-8])$/.test(x)) as TileId[];
    api.getState().patchTiles({ fixedDraws: list.length ? list : undefined });
    const s = Number.parseInt(seedInput, 10) || randomSeed();
    start(s, playerCount);
  };
  const newGame = () => { const s = randomSeed(); setSeedInput(String(s)); start(s, playerCount); };
  const replay = () => { const n = Number.parseInt(seedInput, 10); if (Number.isFinite(n)) start(n, playerCount); };
  const nextHand = () => { sfx.play('uiClick'); g.nextHand(); setRiichiMode(''); setArmed(null); setMsg(''); bump(); };
  const copySeed = () => {
    try { void navigator.clipboard?.writeText(seedInput); setMsg('seed をコピーしました'); }
    catch { setMsg('コピーできませんでした'); }
  };
  // 「新規」二度押し確認 (タッチのみ 2.5s, §10.2)。ポインタはワンクリック。
  const onNewGame = () => {
    sfx.play('uiClick');
    if (coarse && !newArmed) {
      setNewArmed(true);
      if (newTimerRef.current !== null) window.clearTimeout(newTimerRef.current);
      newTimerRef.current = window.setTimeout(() => setNewArmed(false), 2500);
      return;
    }
    if (newTimerRef.current !== null) { window.clearTimeout(newTimerRef.current); newTimerRef.current = null; }
    setNewArmed(false);
    newGame();
  };
  // QoL トグル: settings へ永続化 + 既存 setter (§10.3②)
  const onToggleQol = (key: 'skipCalls' | 'autoWin' | 'autoNuki', v: boolean) => {
    sfx.play('uiClick');
    if (key === 'skipCalls') { patchSettings({ qol: { skipCalls: v } }); g.setSkipCalls(v); }
    else if (key === 'autoWin') { patchSettings({ qol: { autoWin: v } }); g.setAutoWin(v); }
    else { patchSettings({ qol: { autoNuki: v } }); g.setAutoNuki(v); }
    bump();
  };
  const onToggleInfinite = (v: boolean) => {
    api.getState().patchTiles({ infinite: v });
    const s = Number.parseInt(seedInput, 10) || randomSeed();
    start(s, playerCount);
  };
  // CPU方針: collectors only ever COMPLETE a yakuman with infinite tiles — auto-enable 無限牌 + re-deal
  // so picking a target "just works" (現状維持の連動)。
  const onCpuPolicy = (p: string) => {
    setCpuPolicy(p);
    if (p !== 'tsumogiri' && !infinite) {
      api.getState().patchTiles({ infinite: true });
      const s = Number.parseInt(seedInput, 10) || randomSeed();
      start(s, playerCount);
      gameRef.current?.setCpuPolicy?.(p);
    } else {
      g.setCpuPolicy?.(p);
    }
    bump();
  };
  const onPreset = (id: string) => {
    const next = buildPreset(id);
    if (!next) return;
    api.getState().setRuleSet(next);
    const s = Number.parseInt(seedInput, 10) || randomSeed();
    start(s, playerCount);
    setMsg('プリセットを適用して再配牌しました');
  };
  const navigate = (to: 'title' | 'studio' | 'settings') => {
    sfx.play('uiClick');
    setMenuOpen(false);
    setDrawerOpen(false);
    // online 中の離脱は単一の guarded teardown へ集約 (対局中のみ確認・幽霊ローカル卓を作らない)。
    // online でなければ従来どおり画面遷移要求だけを飛ばす。
    if (getOnlineTable()) exitOnline(to as ExitTo, (m) => window.confirm(m));
    else window.dispatchEvent(new CustomEvent('mj:navigate', { detail: to }));
  };
  // online 対局を退出 (盤面の 退出 ボタン): 対局中は確認 → leave 送信+ソケット破棄 → オンラインロビーへ。
  // ローカル solo 卓は作らない (オーナー意味論: 離脱者はロビーへ)。
  const onLeaveOnline = () => {
    sfx.play('uiClick');
    setMenuOpen(false);
    setDrawerOpen(false);
    exitOnline('online', (m) => window.confirm(m));
  };

  const seatLabel = (seat: number) => (seat === me ? 'あなた' : `CPU${seat}`);
  const assist = settings.display.assist; // 聴牌金枠は display.assist 連動 (§10.4)
  const tileSize = Math.round((isXS ? 36 : 40) * settings.display.tileScale); // D17 × tileScale
  const orientedTable = settings.display.orientedTable; // ドロワーと同一ソース (§10.1-9)
  const useOriented = orientedTable && view.players.length <= 4; // SeatBoard と同じ判定
  const hudH = shortLand ? 32 : 40;
  const handKey = `${gameIdRef.current}:${g.kyoku}`; // 配牌カスケードは局単位で再 mount

  // 手牌バンドの 2 パス fit (§3): ① 右トレイ実幅を引いた残りを concealed の可用幅 availW にする →
  // ② fitRow で 1 行に収まる牌幅 handTileW を算出。手牌(≤14)は **扇にしない** ので natural/shrunk のみ
  // 採用し、理論上落ちない fanned でも tileW を使って 1 行・全牌クリック可に保つ (情報省略しない)。
  // tileSize は目標値(natW): 広ければ等倍、狭ければ shrunk で縮む (tileScale 設定は natW に効く)。
  const GAP_BAND = 12; // concealed と右トレイの間 (バンドの gap と一致)
  const DRAWN_MARGIN = drawn ? 14 : 0; // ツモ牌スロットの margin-left
  const HAND_GAP = 6; // concealed の牌間 (.hand-row の inline gap)
  const handCount = handTiles.length + (drawn ? 1 : 0);
  // 計測前 (band/tray=0) は tileSize 等倍にフォールバック (牌は消えない・計測後に縮む)。
  const availW = band.w > 0 ? Math.max(0, band.w - tray.w - (tray.w > 0 ? GAP_BAND : 0) - DRAWN_MARGIN) : 0;
  const handFit = availW > 0
    ? fitRow({ containerW: availW, count: handCount, natW: tileSize, gap: HAND_GAP, minW: 12 })
    : null;
  const handTileW = handFit ? Math.max(12, Math.round(handFit.tileW)) : tileSize;

  const riichiGlow = fx.band !== null
    && (fx.band.tone === 'riichi' || fx.band.tone === 'openRiichi' || fx.band.tone === 'infiniteRiichi');
  const showResult = (g.wait === 'handOver' || g.wait === 'gameOver') && g.result !== null && fx.resultStage >= 0;

  // center-plate の中身 (D16: 場況・ドラの唯一の正出所)。oriented では SeatBoard の中央セル、
  // 縦スタックでは盤面上の帯として同じノードを描く。
  const centerContent = (
    <>
      <div className="round">{WIND[view.roundWind]}{view.dealer + 1}局 {view.honba}本場</div>
      <div className="wall">
        <span className="num">残{infinite ? '∞' : view.wallCount}</span>・{seatLabel(view.dealer)}が親
      </div>
      <div className="dora-well" title="ドラ表示牌">
        {view.doraIndicators.map((t, i) => (
          // A7: 新しい表示牌は mount 時に fx-flip (key=i — 既存はそのまま、増えた分だけめくれる)
          <span key={i} className="fx-flip" style={{ display: 'inline-flex' }}><TileView tile={t} size={16} /></span>
        ))}
      </div>
    </>
  );

  const hudBtn: CSSProperties = {
    flex: '0 0 auto', width: 44, height: '100%', display: 'inline-flex', alignItems: 'center',
    justifyContent: 'center', background: 'none', border: 'none', color: 'var(--text-hi)',
    fontSize: 18, cursor: 'pointer', padding: 0,
  };

  return (
    <div
      ref={rootRef}
      className="table-surface"
      style={{ position: 'relative', flex: '1 1 auto', minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', color: 'var(--text-hi)' }}
    >
      {/* 縦 3 段 (§10.2)。≥1024px は maxWidth:980 センタリング維持 */}
      <div style={{ flex: '1 1 auto', minHeight: 0, width: '100%', maxWidth: 980, margin: '0 auto', display: 'flex', flexDirection: 'column' }}>
        {/* ── HUD: [☰][場況圧縮][人数▾][新規][⚙] ── */}
        <div
          style={{
            flex: '0 0 auto', height: `calc(${hudH}px + env(safe-area-inset-top))`,
            boxSizing: 'border-box', paddingTop: 'env(safe-area-inset-top)',
            paddingLeft: 4, paddingRight: 4,
            display: 'flex', alignItems: 'center', gap: 4,
            zIndex: Z_HUD, background: 'var(--surface-panel)', borderBottom: '1px solid var(--border-panel)',
          }}
        >
          <button type="button" aria-label="メニュー" onClick={() => { sfx.play('uiClick'); setMenuOpen(true); }} style={hudBtn}>☰</button>
          {/* D16: HUD は圧縮テキストのみ。フル場況とドラは center-plate が正 */}
          <span className="num" style={{ flex: '1 1 auto', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'center', fontSize: 12, color: 'var(--text-mid)' }}>
            {WIND[view.roundWind]}{view.dealer + 1}・残{infinite ? '∞' : view.wallCount}
          </span>
          {online ? (
            // online は人数/seed/CPU 等のローカル設定が無い (サーバ権威)。接続状態 + 退出のみ提供する。
            <>
              {remote && <ConnBadge status={remote.connStatus} />}
              {/* HOLE #12: stamp picker (online only, playing phase, no turn gating) */}
              {remote && remote.phase === 'playing' && (
                <div style={{ position: 'relative', flex: '0 0 auto' }}>
                  <button
                    type="button"
                    title="スタンプ"
                    aria-label="スタンプを送る"
                    onClick={() => { sfx.play('uiClick'); setStampPickerOpen((v) => !v); }}
                    style={{ ...FIELD, minWidth: 40, cursor: 'pointer', fontWeight: 700, color: 'var(--text-hi)', padding: '4px 8px' }}
                  >
                    💬
                  </button>
                  {stampPickerOpen && (
                    <>
                      <div
                        aria-hidden="true"
                        onClick={() => setStampPickerOpen(false)}
                        style={{ position: 'fixed', inset: 0, zIndex: 'var(--z-hud)' as CSSProperties['zIndex'] }}
                      />
                      <div
                        style={{
                          position: 'absolute', top: '110%', right: 0, zIndex: Z_MODAL,
                          background: 'var(--surface-modal)', border: '1px solid var(--border-panel)',
                          borderRadius: 10, boxShadow: 'var(--shadow-3)',
                          display: 'grid', gridTemplateColumns: 'repeat(5, 44px)', gap: 4, padding: 8,
                          width: 'max-content',
                        }}
                      >
                        {STAMP_ORDER.map((stamp) => (
                          <button
                            key={stamp}
                            type="button"
                            title={stamp}
                            onClick={() => {
                              sfx.play('uiClick');
                              remote.sendStamp(stamp);
                              setStampPickerOpen(false);
                            }}
                            style={{
                              width: 44, height: 44, fontSize: 22, border: 'none', borderRadius: 8,
                              background: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            {STAMP_LABEL[stamp]}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
              <button
                type="button"
                onClick={onLeaveOnline}
                title="オンライン対局を退出"
                style={{ ...FIELD, flex: '0 0 auto', minWidth: 64, cursor: 'pointer', fontWeight: 700, color: 'var(--text-hi)' }}
              >
                退出
              </button>
            </>
          ) : (
            <>
              <select
                aria-label="人数"
                value={playerCount}
                onChange={(e) => { sfx.play('uiClick'); setPlayerCount(Number(e.target.value) as PlayerCount); }}
                style={{ ...FIELD, flex: '0 0 auto', width: 64 }}
              >
                <option value={1}>1人</option>
                {[2, 3, 4, 5, 6, 7].map((n) => <option key={n} value={n}>{n}人</option>)}
              </select>
              <button
                type="button"
                onClick={onNewGame}
                title="新規対局（ランダム）"
                style={{ ...FIELD, flex: '0 0 auto', minWidth: 64, cursor: 'pointer', fontWeight: 700, color: newArmed ? 'var(--gold-300)' : 'var(--text-hi)' }}
              >
                {newArmed ? 'もう一度' : '新規'}
              </button>
              <button type="button" aria-label="対局設定" onClick={() => { sfx.play('uiClick'); setDrawerOpen(true); }} style={hudBtn}>⚙</button>
            </>
          )}
        </div>

        {/* ── 卓 (SeatBoard)。立直帯の間は fx-riichi-glow を重ねる (A5②) ── */}
        <div
          className={riichiGlow ? 'fx-riichi-glow' : undefined}
          style={{ flex: '1 1 auto', minHeight: shortLand ? 120 : 300, minWidth: 0, display: 'flex', flexDirection: 'column', padding: '4px 8px', zIndex: 0 }}
        >
          {!useOriented && (
            // 縦スタック表示は SeatBoard が中央セルを持たないので、同じ center-plate を上に出す (D16)
            <div style={{ flex: '0 0 auto', display: 'flex', justifyContent: 'center', marginBottom: 4 }}>
              <div className="center-plate" style={{ flexDirection: 'row', alignItems: 'center', gap: 10, padding: '4px 12px' }}>
                {centerContent}
              </div>
            </div>
          )}
          <div style={{ flex: '1 1 auto', minHeight: 0 }}>
            <SeatBoard
              players={seatViewFromView(view, me, remote ? (s) => remote.offlineSeat(s) : undefined)}
              me={me}
              dealer={view.dealer}
              playerCount={view.players.length}
              oriented={orientedTable}
              seatLabel={seatLabel}
              cpuTargetName={(seat) => g.cpuTargetName(seat)}
              center={centerContent}
              riverDelays={fx.riverDelays}
              latestDiscard={fx.latestDiscard}
              deltas={fx.deltas}
              turnSeat={view.turn}
            />
          </div>
        </div>

        {/* ── 手牌ドック (§10 再設計): 手牌行のみを通常フローに置く固定高バンド ── */}
        {/* P2 不変条件: overflow:hidden — ドックは絶対にスクロールしない (手牌は fit で 1 行に収まる)。
            アクション/向聴・近い役は position:absolute のフロートで「ドック上端の外側」に浮かせ、
            バンド高さ (=盤面 flex:1 の取り分) を増やさない。盤面の下端隅に重なるのは可。 */}
        <div className="hand-dock" style={{ flex: '0 0 auto' }}>
          {/* 向聴 + 近い役: 左寄り1行オーバーレイ (display.assist=false なら非表示)。学習補助なので上位数個に絞る。
              選択中 (2タップ打牌) のフィードバックは assist トグルに依らず手番中は常に出す。 */}
          {onTurn && (assist || armed) && (
            <div className="assist-float" aria-live="polite">
              {armed
                ? <span style={{ color: 'var(--gold-300)', fontSize: 'var(--fs-sm)', fontWeight: 700, flex: '0 0 auto' }}>選択中: {tileName(armed.tile)} — もう一度タップで打牌</span>
                : (
                  <span style={{ color: 'var(--text-hi)', fontSize: 'var(--fs-sm)', fontWeight: 700, flex: '0 0 auto' }}>
                    {curShanten === -1 ? '和了形' : curShanten === 0 ? '聴牌' : `${curShanten}向聴`}
                  </span>
                )}
              {!armed && assist && locked && <span style={{ color: '#ffd9d2', fontSize: 'var(--fs-xs)', flex: '0 0 auto' }}>立直中</span>}
              {/* 近い役（推定）: REACHABLE yaku ranked by value — 1行に収めるため上位 3 個 (機能残置 §10) */}
              {!armed && assist && nearYaku.slice(0, 3).map((y) => (
                <span
                  key={y.id}
                  title={`${y.nameEn}：${y.reason}`}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 'var(--fs-xs)',
                    padding: '1px 6px', borderRadius: 999, flex: '0 0 auto',
                    background: y.isYakuman ? 'rgba(224,68,58,.22)' : 'rgba(255,255,255,.12)',
                    border: `1px solid ${y.isYakuman ? '#ffb3aa' : 'rgba(255,255,255,.25)'}`, color: 'var(--text-hi)',
                  }}
                >
                  <strong>{y.nameJa}</strong>
                  <span style={{ color: y.isYakuman ? '#ffd9d2' : 'var(--gold-400)' }}>{y.isYakuman ? '役満' : `${y.value}翻`}</span>
                </span>
              ))}
            </div>
          )}

          {/* 非手番の状態 (CPU手番 / 局終了 / トビ): 左寄り1行フロート。手番フロートと排他なので重ならない。 */}
          {g.wait !== 'turn' && g.wait !== 'call' && (
            <div className="assist-float" aria-live="polite" style={{ color: 'var(--text-mid)', fontSize: 'var(--fs-sm)' }}>
              {g.wait === 'handOver' ? '局終了' : g.wait === 'gameOver' ? '対局終了（トビ）' : 'CPUの手番…'}
            </div>
          )}

          {/* アクションフロート: 右寄りクラスタ。手番時のみ。ツモ/立直 = .btn-primary、他 = .btn-secondary (D12) */}
          {g.wait === 'turn' && (
            <div
              className="action-float"
              style={riichiMode
                ? { background: 'linear-gradient(90deg, rgba(232,193,74,0.28), rgba(232,193,74,0.12))', borderColor: 'var(--border-gold)' }
                : undefined}
            >
              {g.humanCanTsumo() && <button type="button" className="btn-primary" onClick={() => tryAct({ kind: 'tsumo' })}>ツモ</button>}
              {/* 立直モード: pick ONE (立直 / オープン立直 / 無限立直 / オープン無限立直), then discard a tile. */}
              {riichiable && ([
                { id: 'riichi', label: '立直', show: true },
                { id: 'open', label: 'オープン立直', show: rs0.flags.openRiichi === true && !human.riichi?.open },
                { id: 'infinite', label: '無限立直', show: rs0.flags.infiniteRiichi === true },
                { id: 'openInfinite', label: 'オープン無限立直', show: rs0.flags.openRiichi === true && rs0.flags.infiniteRiichi === true && !human.riichi?.open },
              ] as const).filter((m) => m.show).map((m) => (
                <button
                  key={m.id}
                  type="button"
                  className="btn-primary"
                  aria-pressed={riichiMode === m.id}
                  onClick={() => { sfx.play('uiClick'); setArmed(null); setRiichiMode((v) => (v === m.id ? '' : m.id)); }}
                  style={riichiMode === m.id ? { outline: '2px solid #fff', outlineOffset: -3 } : undefined}
                >
                  {riichiMode === m.id ? `${m.label}（牌を選択）` : m.label}
                </button>
              ))}
              {ankans.map((t) => <button key={`ak${t}`} type="button" className="btn-secondary" onClick={() => tryAct({ kind: 'ankan', tile: t })}>暗槓 {tileName(t)}</button>)}
              {shouminkans.map((t) => <button key={`sk${t}`} type="button" className="btn-secondary" onClick={() => tryAct({ kind: 'shouminkan', tile: t })}>加槓 {tileName(t)}</button>)}
              {/* 東西南北の特殊暗槓 (役牌東西南北): only when the wind-set yaku is enabled + all four winds held. */}
              {g.humanCanWindKan() && <button type="button" className="btn-secondary" onClick={() => tryAct({ kind: 'windkan' })}>東西南北槓</button>}
              {/* 北抜き / 西抜き (nukidora) + 花抜き (花牌任意化) */}
              {nukis.map((t) => <button key={`nk${t}`} type="button" className="btn-secondary" onClick={() => tryAct({ kind: 'kita', tile: t })}>{tileName(t)}抜き</button>)}
              {flowerPulls.map((t, i) => <button key={`fp${t}${i}`} type="button" className="btn-secondary" onClick={() => tryAct({ kind: 'flowerpull', tile: t })}>花抜き</button>)}
              {canKyuushuu && <button type="button" className="btn-secondary" onClick={() => tryAct({ kind: 'kyuushuu' })}>九種九牌</button>}
            </div>
          )}

          {/* 手牌バンド: [concealed 手牌(伸縮) | 右トレイ(固定)]。handBandRef/trayRef で 2 パス計測 (§3)。
              これだけを通常フローに置く = ドック高は牌1枚ぶん + 小 padding。 */}
          <div ref={handBandRef} style={{ display: 'flex', alignItems: 'flex-end', gap: GAP_BAND, flexWrap: 'nowrap', minWidth: 0, overflow: 'hidden' }}>
            {/* concealed 手牌は **扇にせず** fitRow(shrunk) のみで常に 1 行・改行なし・全牌クリック可 (.hand-row=nowrap)。
                key=handKey で局ごとに再 mount → fx-deal カスケード。 */}
            <div key={handKey} className="hand-row" style={{ gap: HAND_GAP, flex: '1 1 auto' }}>
              {handTiles.map((t, i) => (
                <HandTile
                  key={`h${t}${i}`}
                  tile={t}
                  size={handTileW}
                  active={onTurn && !locked}
                  tenpai={assist && tenpaiCuts.has(t)}
                  selected={armed?.key === `h${t}${i}`}
                  dealDelay={fx.dealing !== null ? i * 30 : null}
                  onTap={() => tapTile(`h${t}${i}`, t)}
                />
              ))}
              {drawn && (
                // ツモ牌: 常に右端 margin-left:14px。key=journal.head 再 mount + fx-draw-in (§10.5)
                <span key={`dr${g.journal.head}`} className="fx-draw-in" style={{ display: 'inline-flex', marginLeft: DRAWN_MARGIN }}>
                  <HandTile
                    tile={drawn}
                    size={handTileW}
                    active={onTurn}
                    tenpai={assist && tenpaiCuts.has(drawn)}
                    selected={armed?.key === 'drawn'}
                    dealDelay={null}
                    onTap={() => tapTile('drawn', drawn)}
                  />
                </span>
              )}
            </div>
            {(human.melds.length > 0 || (human.kitaCount ?? 0) > 0 || (human.flowers ?? []).length > 0) && (
              <div ref={trayRef} style={{ flex: '0 0 auto', alignSelf: 'flex-end' }}>
                <MeldArea melds={human.melds} kitaCount={human.kitaCount ?? 0} flowers={human.flowers ?? []} seat={me as Seat} players={view.players.length} size={24} />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── 鳴き確認: ボトムアクションシート (z=--z-band、ロン赤強調、パス右端) ── */}
      {g.wait === 'call' && (
        <div
          role="dialog"
          aria-label="鳴き確認"
          style={{
            position: 'absolute', left: 0, right: 0, bottom: 0, zIndex: Z_BAND,
            background: 'var(--surface-modal)', borderTop: '1px solid var(--border-gold)',
            boxShadow: 'var(--shadow-3)', padding: '10px 12px calc(10px + env(safe-area-inset-bottom))',
          }}
        >
          <div style={{ maxWidth: 980, margin: '0 auto', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <strong style={{ fontSize: 13, flex: '0 0 auto' }}>鳴けます</strong>
            {moves.filter((m) => m.kind !== 'pass').map((m, i) => (
              <button
                key={i}
                type="button"
                className={m.kind === 'ron' ? 'btn-primary' : 'btn-secondary'}
                onClick={() => tryAct(m)}
                style={{ minHeight: 56, flex: '0 0 auto', ...(m.kind === 'ron' ? { background: 'var(--c-danger)', borderColor: '#8c1f1f', color: '#fff' } : {}) }}
              >
                {callLabel(m)}
              </button>
            ))}
            <button type="button" className="btn-secondary" style={{ minHeight: 56, marginLeft: 'auto', flex: '0 0 auto' }} onClick={() => { sfx.play('uiClick'); g.pass(); setArmed(null); bump(); }}>パス</button>
          </div>
        </div>
      )}

      {/* ── 結果: 段階開示モーダル (fx.resultStage 制御、タップで skip 早送り) ── */}
      {showResult && (
        <ResultModal
          g={g}
          stage={fx.resultStage}
          reduced={reduced}
          resultName={resultName}
          limitMode={limitMode}
          seatLabel={seatLabel}
          onNext={nextHand}
          onSkip={skip}
          dailyReward={dailyReward}
        />
      )}

      {/* ── HOLE #12: stamp bubbles — absolute float, does NOT grow the dock height ── */}
      {remote && (() => {
        const now = Date.now();
        const active = remote.stamps.filter((s) => now - s.at < 4000);
        if (active.length === 0) return null;
        return (
          <div
            aria-live="polite"
            style={{
              position: 'absolute', top: `calc(${hudH}px + env(safe-area-inset-top) + 8px)`, right: 8,
              zIndex: Z_MODAL, display: 'flex', flexDirection: 'column', gap: 4, pointerEvents: 'none',
            }}
          >
            {active.map((s, i) => (
              <div
                key={i}
                style={{
                  background: 'var(--surface-modal)', border: '1px solid var(--border-panel)',
                  borderRadius: 20, padding: '4px 12px', fontSize: 14, display: 'flex',
                  alignItems: 'center', gap: 6, boxShadow: 'var(--shadow-3)',
                  color: 'var(--text-hi)', whiteSpace: 'nowrap',
                }}
              >
                <span style={{ fontSize: 11, color: 'var(--text-mid)' }}>{seatLabel(s.seat)}</span>
                <span style={{ fontSize: 20 }}>{STAMP_LABEL[s.stamp]}</span>
              </div>
            ))}
          </div>
        );
      })()}

      {/* ── 演出オーバーレイ (帯 / 役満カットイン / 配牌バナー / スキップ層 / aria-live) ── */}
      <FxOverlay fx={fx} onSkip={skip} ariaLines={ariaLines} />

      {/* ── online 終端 (fatal): 部屋削除/チケット失効等。再接続は止まっている → 前進操作のみ ── */}
      {remote?.fatal && <FatalModal reason={remote.fatal} />}

      {/* ── ☰ メニューシート (App の screen 切替へ依頼: mj:navigate) ── */}
      {menuOpen && (
        <>
          <div aria-hidden="true" onClick={() => setMenuOpen(false)} style={{ position: 'absolute', inset: 0, zIndex: Z_MODAL, background: 'rgba(0,0,0,0.35)' }} />
          <div
            role="menu"
            aria-label="対局メニュー"
            style={{
              position: 'absolute', top: `calc(${hudH}px + env(safe-area-inset-top) + 4px)`, left: 8, zIndex: Z_MODAL,
              minWidth: 200, overflow: 'hidden', background: 'var(--surface-modal)',
              border: '1px solid var(--border-panel)', borderRadius: 10, boxShadow: 'var(--shadow-3)',
            }}
          >
            {([
              { label: '対局に戻る', act: () => { sfx.play('uiClick'); setMenuOpen(false); } },
              { label: '工房', act: () => navigate('studio') },
              { label: '設定', act: () => navigate('settings') },
              { label: 'タイトルへ', act: () => navigate('title') },
            ] as const).map((it) => (
              <button
                key={it.label}
                type="button"
                role="menuitem"
                onClick={it.act}
                style={{ display: 'block', width: '100%', minHeight: 48, padding: '0 16px', textAlign: 'left', background: 'none', border: 'none', borderBottom: '1px solid var(--border-panel)', color: 'var(--text-hi)', fontSize: 14, cursor: 'pointer', fontFamily: 'inherit' }}
              >
                {it.label}
              </button>
            ))}
          </div>
        </>
      )}

      {/* ── 対局設定ドロワー (§10.3) — ローカル専用 (online では非表示) ── */}
      {!online && <GameSettingsDrawer
        open={drawerOpen}
        onClose={closeDrawer}
        seedInput={seedInput}
        setSeedInput={setSeedInput}
        doraInput={doraInput}
        setDoraInput={setDoraInput}
        drawsInput={drawsInput}
        setDrawsInput={setDrawsInput}
        cpuPolicy={cpuPolicy}
        infinite={infinite}
        yakumanTargets={yakumanTargets}
        onPreset={onPreset}
        onToggleQol={onToggleQol}
        onToggleInfinite={onToggleInfinite}
        onCpuPolicy={onCpuPolicy}
        onReplay={replay}
        onCopySeed={copySeed}
        onApplyDora={applyDora}
        onApplyDraws={applyDraws}
        onOpenStudio={() => navigate('studio')}
      />}

      {/* ── msg トースト (§10.1-16): エラー・seed コピー等。3 秒で自動消滅 ── */}
      {msg && <div className="toast" role="status" aria-live="polite">{msg}</div>}

      {/* ── HOLE #19: 席役牌ヒントチップ (段階開示 / N>4 初回のみ) ──
           inline overlay: 横画面 fit-safe, 改行なし 1行, theme.css 未変更。
           モーダルでなく絶対配置チップ。OrientationGate の内側 (このコンポーネントが
           OrientationGate に包まれているため自動的に横画面保証済み)。 */}
      {seatYakuHint && (
        <div
          role="status"
          aria-live="polite"
          style={{
            position: 'absolute',
            bottom: 'calc(64px + env(safe-area-inset-bottom))',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: Z_BAND,
            background: 'var(--surface-panel)',
            color: 'var(--text-hi)',
            border: '1px solid var(--border-panel)',
            borderRadius: 8,
            padding: '8px 14px',
            fontSize: 13,
            lineHeight: 1.4,
            whiteSpace: 'nowrap',
            maxWidth: 'min(92vw, 480px)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            boxShadow: '0 2px 8px rgba(0,0,0,0.35)',
          }}
        >
          <span style={{ flex: '1 1 auto', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {seatYakuHint}
          </span>
          <button
            type="button"
            aria-label="閉じる"
            onClick={() => { markSeen('seatYaku'); setSeatYakuHint(null); }}
            style={{
              flex: '0 0 auto', background: 'none', border: 'none',
              color: 'var(--text-mid)', fontSize: 16, cursor: 'pointer',
              padding: '0 2px', lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// 手牌の 1 牌 — button ラッパ (タップ領域高さ ≥44px・行高 52px) + .tile-btn 状態は theme.css。
// クリック抑止は active=false 時に onClick を外す (aria-disabled は TileView が描画減彩)。
// ---------------------------------------------------------------------------
function HandTile({ tile, size, active, tenpai, selected, dealDelay, onTap }: {
  tile: TileId; size: number; active: boolean; tenpai: boolean; selected: boolean;
  dealDelay: number | null; onTap: () => void;
}) {
  return (
    <button
      type="button"
      onClick={active ? onTap : undefined}
      aria-disabled={!active}
      aria-label={`${tileName(tile)}を打牌`}
      title={tenpai ? 'これを切ると聴牌' : undefined}
      className={dealDelay !== null ? 'fx-deal' : undefined}
      style={{
        minHeight: 52, padding: 0, border: 'none', background: 'none',
        display: 'inline-flex', alignItems: 'flex-end', cursor: active ? 'pointer' : 'default',
        animationDelay: dealDelay !== null ? `${dealDelay}ms` : undefined,
      }}
    >
      <TileView tile={tile} size={size} className="tile-btn" selected={selected} tenpai={tenpai} disabled={!active} />
    </button>
  );
}

// 花/北抜き PILE 扇 (TileFan) の box 上限 (tileSize × この列数)。これを超える分は扇で重ね、×N を併記
// する → トレイが横へ無限に広がらず (no-wrap・空間有界)、かつ牌は1枚も消えない。SeatBoard の
// MeldStrip と同値 (= 自分/他家で同じ扇規約)。固定上限なので右トレイ幅は有界 → §3 の 2 パスが安定する。
const PILE_FAN_COLS = 7;

// A player's called area: 副露 (oriented via MeldView) + 北抜き (kitaCount × North) + 花牌, laid left→right.
// 北抜き/花牌は無制限に増えうる PILE → TileFan で扇 (下限寸法でも溢れる時のみ重ね、×N を併記)。box は
// 固定上限 (size×PILE_FAN_COLS) に bound してトレイが無限に広がらないようにする (no-wrap・空間有界)。
// 牌は1枚も消えない。副露 (MeldView) は手構成で有界なので nowrap で素直に並べる (flex-wrap 撤廃)。
function MeldArea({ melds, kitaCount, flowers, seat, players, size }: {
  melds: Meld[]; kitaCount: number; flowers: TileId[]; seat: Seat; players: number; size: number;
}) {
  if (melds.length === 0 && kitaCount === 0 && flowers.length === 0) return null;
  const fanBoxW = size * PILE_FAN_COLS;
  const kitaTiles: TileId[] = kitaCount > 0 ? Array.from({ length: kitaCount }, () => 'z4' as TileId) : [];
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, flexWrap: 'nowrap', minWidth: 0 }}>
      {melds.map((m, mi) => <MeldView key={mi} meld={m} ownerSeat={seat} players={players} size={size} />)}
      {kitaCount > 0 && (
        <TileFan tiles={kitaTiles} boxW={fanBoxW} tileSize={size} title={`北抜き ×${kitaCount}`} />
      )}
      {flowers.length > 0 && (
        <TileFan tiles={flowers} boxW={fanBoxW} tileSize={size} title="花牌（ドラ）" />
      )}
    </div>
  );
}

function callLabel(m: Intent): string {
  switch (m.kind) {
    case 'ron': return 'ロン';
    case 'pon': return 'ポン';
    case 'minkan': return 'カン';
    case 'chii': return `チー ${m.tiles.join('')}`;
    default: return m.kind;
  }
}

// ---------------------------------------------------------------------------
// online: 接続バッジ (HUD)。open=緑/connecting=接続中/closed=再接続中… (RemoteTable.connStatus 連動)。
// ---------------------------------------------------------------------------
function ConnBadge({ status }: { status: 'connecting' | 'open' | 'closed' }) {
  const cfg = status === 'open'
    ? { dot: 'var(--c-win, #3fb950)', label: '接続中', color: 'var(--text-mid)' }
    : status === 'connecting'
      ? { dot: 'var(--gold-400, #e8c14a)', label: '接続中…', color: 'var(--text-mid)' }
      : { dot: 'var(--c-danger, #e5484d)', label: '再接続中…', color: 'var(--c-danger, #e5484d)' };
  return (
    <span
      title={`接続: ${status}`}
      style={{ flex: '0 0 auto', display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, color: cfg.color, whiteSpace: 'nowrap' }}
    >
      <span style={{ width: 8, height: 8, borderRadius: '50%', background: cfg.dot, flex: '0 0 auto' }} aria-hidden />
      {cfg.label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// online: 終端 (fatal) モーダル — 再接続は止まっている。前進操作 (ロビー/タイトル) のみ。
// ---------------------------------------------------------------------------
function FatalModal({ reason }: { reason: string }) {
  return (
    <div style={{ position: 'absolute', inset: 0, zIndex: Z_MODAL, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.55)', padding: 16 }}>
      <div className="modal-card" style={{ padding: '20px 22px', maxWidth: 420, display: 'flex', flexDirection: 'column', gap: 14, textAlign: 'center' }}>
        <strong style={{ fontSize: 16, color: 'var(--c-danger, #e5484d)' }}>接続が切断されました</strong>
        <div style={{ fontSize: 14, color: 'var(--text-mid)', lineHeight: 1.6 }}>{errJa(reason)}</div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
          <button type="button" className="btn-primary" style={{ minHeight: 48, minWidth: 140 }} onClick={() => exitOnline('online')}>
            オンラインロビーへ
          </button>
          <button type="button" className="btn-secondary" style={{ minHeight: 48, minWidth: 120 }} onClick={() => exitOnline('title')}>
            タイトルへ
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// rAF カウントアップ数値 (A8④⑤の視覚側。tick 音と段階ピッチは fx.ts が発火済み)。
// instant=true (reduced / skip 済み) は最終値を即表示。easeOutExpo。
// ---------------------------------------------------------------------------
function RollingNum({ value, durationMs, instant }: { value: number; durationMs: number; instant: boolean }) {
  const [v, setV] = useState(instant ? value : 0);
  useEffect(() => {
    if (instant) { setV(value); return; }
    let raf = 0;
    const t0 = performance.now();
    const ease = (x: number) => (x >= 1 ? 1 : 1 - Math.pow(2, -10 * x));
    const step = (t: number) => {
      const p = Math.min(1, (t - t0) / durationMs);
      setV(Math.round(value * ease(p)));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [value, durationMs, instant]);
  return <span>{v.toLocaleString()}</span>;
}

// ---------------------------------------------------------------------------
// 結果モーダル (§10.4) — 段階開示: stage 0=モーダル, 1..n=役行, n+1=翻, n+2=点, n+3=移動 (final)。
// reduced 時は 0→final 直行 (fx 契約) なので「stage ≥ しきい値で表示」型で実装する。
// モーダル中のタップは skip() (早送り) — FxOverlay の透明層はモーダルを塞がない分担 (§7.5)。
// ---------------------------------------------------------------------------
function ResultModal({ g, stage, reduced, resultName, limitMode, seatLabel, onNext, onSkip, dailyReward }: {
  g: PlayTable;
  stage: number;
  reduced: boolean;
  resultName: (id: string, han: number) => string;
  limitMode: 'capped' | 'aotenjou' | 'stepped';
  seatLabel: (seat: number) => string;
  onNext: () => void;
  onSkip: () => void;
  dailyReward: { en: number; progressPt: number; progress: DailyProgress } | null;
}) {
  const r = g.result!;
  const ev = r.detail;
  const win = ev !== null && ev.event === 'win' ? ev : null;
  // 段階数は先頭 winner の役行数で決まる (fx.ts planFx と同じ規約)
  const rows0 = win !== null ? (win.results[0]?.yaku.length ?? 0) : 0;
  const finalStage = win !== null ? rows0 + 3 : 1;
  const final = stage >= finalStage;
  const popCls = reduced ? undefined : 'fx-pop-in';
  return (
    <div
      onClick={() => { if (!final) onSkip(); else if (g.wait !== 'gameOver') onNext(); }}
      style={{ position: 'absolute', inset: 0, zIndex: Z_MODAL, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.45)', padding: 16 }}
    >
      <div className={popCls ? `modal-card ${popCls}` : 'modal-card'} style={{ padding: '16px 18px', maxHeight: '82dvh', overflowY: 'auto' }}>
        {win !== null ? win.winners.map((w: Seat, wi: number) => {
          const sr = win.results[wi];
          if (!sr) return null;
          const limit = limitLabelFor(sr, limitMode);
          return (
            <div key={w} style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <strong style={{ fontSize: 15 }}>{seatLabel(w)} 和了</strong>
                {stage >= rows0 + 2 && limit && <span style={{ color: 'var(--c-win)', fontWeight: 700 }}>{limit}</span>}
              </div>
              <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 3 }}>
                {sr.yaku.map((y, ri) => stage >= ri + 1 && (
                  <div key={`${y.id}${ri}`} className={popCls} style={{ display: 'flex', justifyContent: 'space-between', gap: 16, fontSize: 13 }}>
                    <span>{resultName(y.id, y.han)}</span>
                    <span className="num" style={{ color: 'var(--gold-300)', fontWeight: 700 }}>{y.han}翻</span>
                  </div>
                ))}
              </div>
              {stage >= rows0 + 1 && (
                <div className={popCls} style={{ marginTop: 6, display: 'flex', alignItems: 'baseline', gap: 10 }}>
                  <span className="num" style={{ fontWeight: 700 }}>
                    計 <RollingNum value={sr.han} durationMs={Math.min(80 * Math.max(1, sr.han), 800)} instant={reduced || stage > rows0 + 1} />翻
                  </span>
                  <span className="num" style={{ color: 'var(--text-mid)', fontSize: 12 }}>{sr.fu}符</span>
                  <span style={{ color: 'var(--text-mid)', fontSize: 12 }}>{doraSummary(sr.doraBreakdown)}</span>
                </div>
              )}
              {stage >= rows0 + 2 && (
                // 点数表は紙面 (--surface-ivory, §10.4)
                <div className={popCls} style={{ marginTop: 8, background: 'var(--surface-ivory)', color: 'var(--text-on-ivory)', borderRadius: 8, padding: '8px 12px', display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
                  <span style={{ fontWeight: 700, fontSize: 13 }}>{limit || '和了'}</span>
                  <span className="num" style={{ fontSize: 'var(--fs-score)', fontWeight: 700 }}>
                    <RollingNum value={sr.points} durationMs={700} instant={reduced || stage > rows0 + 2} />点
                  </span>
                </div>
              )}
              {stage >= rows0 + 3 && (
                <div className="num" style={{ marginTop: 6, fontSize: 12, color: 'var(--text-mid)' }}>移動 [{sr.payments.join(', ')}]</div>
              )}
            </div>
          );
        }) : (
          <div>
            <strong style={{ fontSize: 15 }}>流局</strong>
            {ev && ev.event === 'ryuukyoku' && (
              <div style={{ fontSize: 13, marginTop: 4 }}>テンパイ: {ev.tenpai.map(seatLabel).join('・') || 'なし'}</div>
            )}
            {stage >= 1 && ev && ev.event === 'ryuukyoku' && (
              <div className="num" style={{ fontSize: 12, color: 'var(--text-mid)', marginTop: 4 }}>移動 [{ev.payments.join(', ')}]</div>
            )}
          </div>
        )}
        {/* HOLE #20: daily reward inline toast (additive, no new screen/tab).
            Shows 圓 grant + 立ち絵-progress after the match-end result, only on final stage.
            Uses inline styles (no theme.css change). Dynamic sizing — does not add scroll. */}
        {final && dailyReward && (dailyReward.en > 0 || dailyReward.progressPt > 0) && (() => {
          const face = progressToFace(dailyReward.progress.lifetimeProgressPt);
          const remaining = face.ptForNextStep - face.ptIntoStep;
          return (
            <div
              style={{
                marginTop: 10, padding: '8px 12px', borderRadius: 8,
                background: 'rgba(232,193,74,0.12)', border: '1px solid rgba(232,193,74,0.35)',
                display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
              }}
            >
              <span style={{ fontSize: 12, color: 'var(--text-mid)', flex: '0 0 auto' }}>今日のデイリー</span>
              {dailyReward.en > 0 && (
                <span className="num" style={{ fontSize: 13, fontWeight: 700, color: 'var(--gold-300)', flex: '0 0 auto' }}>+{dailyReward.en}圓</span>
              )}
              {dailyReward.progressPt > 0 && (
                <span style={{ fontSize: 12, color: 'var(--text-hi)', flex: '0 0 auto' }}>立ち絵まであと{remaining}pt</span>
              )}
            </div>
          );
        })()}
        {g.wait === 'gameOver'
          ? <div style={{ marginTop: 10, fontWeight: 700, color: 'var(--c-danger)' }}>対局終了（トビ）</div>
          : final
            ? <button type="button" className="btn-primary" style={{ width: '100%', minHeight: 56, marginTop: 12, fontSize: 15 }} onClick={(e) => { e.stopPropagation(); onNext(); }}>次局へ</button>
            : <div style={{ marginTop: 12, fontSize: 11, color: 'var(--text-low)', textAlign: 'center' }}>タップで早送り</div>}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// QoL トグル行 (§10.3②): 行高 48px・スイッチ 44×24px・副文 11px 常時表示。
// ---------------------------------------------------------------------------
function ToggleRow({ label, sub, checked, onChange }: { label: string; sub: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <div style={{ minHeight: 48, display: 'flex', alignItems: 'center', gap: 10 }}>
      <div style={{ flex: '1 1 auto', minWidth: 0 }}>
        <div style={{ fontSize: 13 }}>{label}</div>
        <div style={{ fontSize: 11, color: 'var(--text-mid)' }}>{sub}</div>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        onClick={() => onChange(!checked)}
        style={{
          flex: 'none', width: 44, height: 24, padding: 0, position: 'relative', cursor: 'pointer',
          borderRadius: 999, border: '1px solid var(--border-panel)',
          background: checked ? 'var(--gold-500)' : 'rgba(255,255,255,0.18)',
        }}
      >
        <span style={{ position: 'absolute', top: 2, left: checked ? 22 : 2, width: 18, height: 18, borderRadius: '50%', background: '#fff', transition: 'left 120ms' }} />
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 対局設定ドロワー (§10.3): <768 ボトムシート / ≥768 右 360px (.drawer-sheet が CSS で切替)。
// focus trap・Esc・バックドロップ・(ハンドルの) スワイプダウンで閉じる。
// 中身順: ①プリセット ②QoL 3 種 ③回転卓 ④工房リンク ⑤検証ツールアコーディオン (.dev-drawer)。
// ---------------------------------------------------------------------------
function GameSettingsDrawer(props: {
  open: boolean;
  onClose: () => void;
  seedInput: string; setSeedInput: (v: string) => void;
  doraInput: string; setDoraInput: (v: string) => void;
  drawsInput: string; setDrawsInput: (v: string) => void;
  cpuPolicy: string;
  infinite: boolean;
  yakumanTargets: { id: string; ja: string }[];
  onPreset: (id: string) => void;
  onToggleQol: (key: 'skipCalls' | 'autoWin' | 'autoNuki', v: boolean) => void;
  onToggleInfinite: (v: boolean) => void;
  onCpuPolicy: (p: string) => void;
  onReplay: () => void;
  onCopySeed: () => void;
  onApplyDora: () => void;
  onApplyDraws: () => void;
  onOpenStudio: () => void;
}) {
  const { open, onClose } = props;
  const settings = useSettings();
  const ref = useRef<HTMLDivElement>(null);
  const swipeY = useRef<number | null>(null);
  const [pendingPreset, setPendingPreset] = useState<string | null>(null);

  // focus trap + Esc (§10.3)。開いたら先頭へフォーカス、閉じたら元の要素へ返す。
  useEffect(() => {
    if (!open) return;
    const root = ref.current;
    if (!root) return;
    const prev = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const sel = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
    const focusables = () => [...root.querySelectorAll<HTMLElement>(sel)].filter((el) => !el.hasAttribute('disabled'));
    focusables()[0]?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key !== 'Tab') return;
      const items = focusables();
      if (items.length === 0) return;
      const first = items[0]!;
      const last = items[items.length - 1]!;
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('keydown', onKey); prev?.focus(); };
  }, [open, onClose]);
  useEffect(() => { if (!open) setPendingPreset(null); }, [open]);

  if (!open) return null;

  const sectionH: CSSProperties = { fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', color: 'var(--text-low)', margin: '14px 0 6px' };
  const onHandleDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    swipeY.current = e.clientY;
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onHandleMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (swipeY.current !== null && e.clientY - swipeY.current > 60) { swipeY.current = null; onClose(); }
  };

  return (
    <>
      <div aria-hidden="true" onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: Z_MODAL, background: 'rgba(0,0,0,0.45)' }} />
      {/* inline padding が class の safe-area padding-bottom を上書きするので env をここで合成する */}
      <div ref={ref} className="drawer-sheet" role="dialog" aria-modal="true" aria-label="対局設定" style={{ padding: '0 16px calc(16px + env(safe-area-inset-bottom))' }}>
        {/* スワイプダウン領域 (ボトムシート時のドラッグハンドル) */}
        <div onPointerDown={onHandleDown} onPointerMove={onHandleMove} onPointerUp={() => { swipeY.current = null; }} style={{ touchAction: 'none', cursor: 'grab' }}>
          <div className="drawer-handle" />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <h2 style={{ fontSize: 15, margin: 0, flex: '1 1 auto' }}>対局設定</h2>
          <button type="button" aria-label="閉じる" onClick={onClose} style={{ width: 44, height: 44, background: 'none', border: 'none', color: 'var(--text-mid)', fontSize: 18, cursor: 'pointer' }}>✕</button>
        </div>

        {/* ① ルールプリセット (カード型ラジオ + 確認) */}
        <div style={sectionH}>ルールプリセット</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {RULESET_PRESETS.map((p) => (
            <button
              key={p.id}
              type="button"
              aria-pressed={pendingPreset === p.id}
              onClick={() => setPendingPreset((v) => (v === p.id ? null : p.id))}
              style={{
                textAlign: 'left', padding: '10px 12px', borderRadius: 10, cursor: 'pointer', fontFamily: 'inherit',
                background: 'var(--surface-card)', color: 'var(--text-hi)',
                border: `1px solid ${pendingPreset === p.id ? 'var(--gold-400)' : 'var(--border-panel)'}`,
              }}
            >
              <div style={{ fontSize: 13, fontWeight: 700 }}>{p.name.ja}</div>
              <div style={{ fontSize: 11, color: 'var(--text-mid)', marginTop: 2 }}>{p.description.ja}</div>
            </button>
          ))}
          {pendingPreset !== null && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--text-mid)', flexWrap: 'wrap' }}>
              このルールで再配牌します
              <button type="button" className="btn-primary" style={{ minHeight: 44 }} onClick={() => { props.onPreset(pendingPreset); setPendingPreset(null); }}>適用</button>
              <button type="button" className="btn-secondary" style={{ minHeight: 44 }} onClick={() => setPendingPreset(null)}>キャンセル</button>
            </div>
          )}
        </div>

        {/* ② QoL トグル 3 種 (settings.qol 永続化 + 既存 setter) */}
        <div style={sectionH}>クオリティ・オブ・ライフ</div>
        <ToggleRow
          label="鳴き確認スキップ"
          sub="鳴き（ポン/チー/カン）の確認を出さず自動でパス。ロンは出ます"
          checked={settings.qol.skipCalls}
          onChange={(v) => props.onToggleQol('skipCalls', v)}
        />
        <ToggleRow
          label="自動和了"
          sub="和了可能なら自動でツモ/ロンを宣言"
          checked={settings.qol.autoWin}
          onChange={(v) => props.onToggleQol('autoWin', v)}
        />
        <ToggleRow
          label="自動抜き"
          sub="抜き牌（人数に応じ北/西/南）を自動で抜く。OFFで手動（抜かない選択も可）"
          checked={settings.qol.autoNuki}
          onChange={(v) => props.onToggleQol('autoNuki', v)}
        />

        {/* ③ 回転卓 (display.orientedTable と同一ソース) */}
        <ToggleRow
          label="回転卓"
          sub="4人以下で卓を回転表示（下=自分、時計回りに右/上/左）。OFFで縦リスト固定。5/6人は常に縦リスト"
          checked={settings.display.orientedTable}
          onChange={(v) => { sfx.play('uiClick'); patchSettings({ display: { orientedTable: v } }); }}
        />

        {/* ④ 詳細ルールは工房で */}
        <button type="button" onClick={props.onOpenStudio} style={{ marginTop: 6, padding: '8px 0', background: 'none', border: 'none', color: 'var(--gold-300)', fontSize: 13, cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit' }}>
          詳細ルールは工房で →
        </button>

        {/* ⑤ 検証ツール (.dev-drawer アコーディオン。dev.showTools=false で非表示 = 隠すだけで消さない) */}
        {settings.dev.showTools && (
          <div className="dev-drawer" style={{ marginTop: 14, padding: '2px 10px 10px' }}>
            <button
              type="button"
              aria-expanded={settings.dev.toolsOpen}
              onClick={() => { sfx.play('uiClick'); patchSettings({ dev: { toolsOpen: !settings.dev.toolsOpen } }); }}
              style={{ width: '100%', minHeight: 44, display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'none', border: 'none', color: 'var(--text-hi)', fontSize: 13, fontWeight: 700, cursor: 'pointer', padding: 0, fontFamily: 'inherit' }}
            >
              <span>🔧 検証ツール</span>
              <span aria-hidden="true">{settings.dev.toolsOpen ? '▾' : '▸'}</span>
            </button>
            {settings.dev.toolsOpen && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 4 }}>
                {/* seed + 再現 + コピー (§10.1-10) */}
                <div>
                  <label style={{ fontSize: 12, color: 'var(--text-mid)', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    seed
                    <input value={props.seedInput} onChange={(e) => props.setSeedInput(e.target.value)} inputMode="numeric" style={{ ...FIELD, width: 110 }} />
                    <button type="button" className="btn-secondary" style={{ minHeight: 36, padding: '4px 10px', fontSize: 12 }} onClick={props.onReplay}>再現</button>
                    <button type="button" className="btn-secondary" style={{ minHeight: 36, padding: '4px 10px', fontSize: 12 }} onClick={props.onCopySeed}>コピー</button>
                  </label>
                </div>
                {/* 無限牌 (§10.1-11): rules ARE data — toggle the working ruleset and re-deal. */}
                <ToggleRow
                  label="無限牌"
                  sub="無限供給・流局なし・累積式（天地創世まで）。切替で再配牌"
                  checked={props.infinite}
                  onChange={(v) => { sfx.play('uiClick'); props.onToggleInfinite(v); }}
                />
                {/* CPU方針 (§10.1-12): 機械的役満コレクター (思考しない)。警告 2 種はこの直下にインライン (§10.1-15) */}
                <div>
                  <label style={{ fontSize: 12, color: 'var(--text-mid)', display: 'flex', alignItems: 'center', gap: 6 }} title="CPUは思考しません。指定役満の牌を集めて他を切るだけの機械的コレクターです">
                    CPU方針
                    <select value={props.cpuPolicy} onChange={(e) => props.onCpuPolicy(e.target.value)} style={{ ...FIELD, flex: '1 1 auto', minWidth: 0 }}>
                      <option value="tsumogiri">ツモ切り</option>
                      <option value="closest">一番近い役満狙い</option>
                      <option value="random">ランダム役満狙い</option>
                      <option value="double">ダブル役満以上のみ</option>
                      {props.yakumanTargets.map((t) => <option key={t.id} value={t.id}>{t.ja}狙い</option>)}
                    </select>
                  </label>
                  {props.infinite && props.cpuPolicy === 'tsumogiri' && (
                    <div style={{ fontSize: 11, color: 'var(--c-danger)', fontWeight: 700, marginTop: 4 }} title="無限牌は流局しないので、ツモ切り同士だと局が永遠に終わりません">
                      ⚠ 無限牌＋ツモ切りでは誰も和了しません（局が終わらない）。CPUに役満を選んでください
                    </div>
                  )}
                  {props.cpuPolicy !== 'tsumogiri' && !props.infinite && (
                    <div style={{ fontSize: 11, color: 'var(--c-danger)', marginTop: 4 }} title="有限の壁では4人が牌を食い合い、役満はほぼ揃いません">
                      ⚠ 有限壁では役満はほぼ揃いません（無限牌推奨）
                    </div>
                  )}
                </div>
                {/* ドラ指定 (§10.1-13): Enter 対応・バリデーション現状維持 */}
                <label style={{ fontSize: 12, color: 'var(--text-mid)', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }} title="ドラ表示牌を指定（カンマ区切り tile id 例 z1,p5,m9）。空で通常ランダム。Enterか適用で再配牌">
                  ドラ指定
                  <input value={props.doraInput} onChange={(e) => props.setDoraInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') props.onApplyDora(); }} placeholder="例 z1,p5" style={{ ...FIELD, width: 110 }} />
                  <button type="button" className="btn-secondary" style={{ minHeight: 36, padding: '4px 10px', fontSize: 12 }} onClick={props.onApplyDora}>適用</button>
                </label>
                {/* 初期自摸 (§10.1-14): force seat 0's draw sequence */}
                <label style={{ fontSize: 12, color: 'var(--text-mid)', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }} title="自分のツモ順を指定（カンマ区切り tile id 例 m1,m2,m3）。空で通常。Enterか適用で再配牌">
                  初期自摸
                  <input value={props.drawsInput} onChange={(e) => props.setDrawsInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') props.onApplyDraws(); }} placeholder="例 m1,m2" style={{ ...FIELD, width: 110 }} />
                  <button type="button" className="btn-secondary" style={{ minHeight: 36, padding: '4px 10px', fontSize: 12 }} onClick={props.onApplyDraws}>適用</button>
                </label>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}
